<script>
import PinCodeInput from 'vue-pincode-input'

export default {
  name: 'AuthFormVerificationCode',
  components: {
    PinCodeInput,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: '',
    },
    otpLength: {
      type: Number,
      default: 6,
    },
    resendVisible: {
      type: Boolean,
      default: false,
    },
    disabledInput: {
      type: Boolean,
      default: false,
    },
    otpTimer: {
      type: Number,
      default: 60,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    submittingResend: {
      type: Boolean,
      default: false,
    },
    errorKey: {
      type: String,
      default: '',
    },
    errorKeyResend: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      otp: '',
    }
  },
  computed: {
    shouldHasSlash() {
      return this.otpLength === 6
    },
    shouldFillSlash() {
      if (this.otpLength !== 6) {
        return false
      }

      return this.otp.length >= 3
    },
    errorMessage() {
      if (!this.errorKey && !this.errorKeyResend) {
        return ''
      }

      return this.$t(this.errorKey) || this.$t(this.errorKeyResend)
    },
    isDisabledInput() {
      return this.submitting || this.submittingResend || this.disabledInput
    },
    isDisabledResend() {
      return this.submitting || this.submittingResend
    },
  },
  watch: {
    otp(newVal) {
      if (newVal.length === this.otpLength) {
        this.submitVerificationCode()
      }

      this.$track.trackOnce('login_page_post_login_otp_pop_up_otp_interacted')
    },
  },
  mounted() {
    this.$emit('mounted')
  },
  methods: {
    async submitVerificationCode() {
      this.$emit('submit:verification-code', { verificationCode: this.otp })
    },
    onClickResendCode() {
      this.otp = ''

      this.$emit('resend')
    },
  },
}
</script>

<template>
  <div class="px-5 tamara-login-input-otp">
    <div class="tamara-login-input-otp__title">
      {{ title }}
    </div>
    <div class="tamara-login-input-otp__description">
      <slot name="description">
        {{ description }}
      </slot>
    </div>
    <div
      class="tamara-login-input-otp__input"
      :class="{
        'filled-slash': shouldFillSlash,
        'no-slash': !shouldHasSlash,
        'is-disabled': isDisabledInput,
      }"
    >
      <div dir="ltr" class="tamara-login-input-otp__input__pin-code">
        <PinCodeInput
          ref="pinCodeInput"
          v-model="otp"
          :length="otpLength"
          :disabled="isDisabledInput"
          placeholder="0"
        />
        <div class="tamara-login-input-otp__input__slash">
          -
        </div>
      </div>
    </div>
    <Transition name="fade">
      <div v-if="errorMessage" class="tamara-login-input-otp__error">
        <svg-icon
          icon-class="warning-circle-lavender"
          width="24px"
          height="24px"
        />
        <span>{{ errorMessage }}</span>
      </div>
    </Transition>
    <div v-if="resendVisible" class="tamara-login-input-otp__footer">
      <div class="tamara-login-input-otp__footer__resend">
        {{ $t('otp_not_receive_code') }}
        <div class="tamara-login-input-otp__footer__resend__link-wrapper">
          <template v-if="otpTimer > 0">
            {{ $t('onboarding_verification_resend') }}
            {{ otpTimer }}
          </template>
          <template v-if="otpTimer === 0">
            <span
              class="tamara-login-input-otp__footer__resend__link"
              :class="{ 'is-disabled': isDisabledResend }"
              @click="onClickResendCode"
            >
              {{ $t('onboarding_verification_resend_text') }}
            </span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/lavender/design.scss';
@import '@/styles/_breakpoints.scss';

.tamara-login-input-otp {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  padding-top: 4px;
  background-color: var(--tamara-background-color-primary);
  border-radius: var(--tamara-border-radius-lg);

  &__title {
    @extend .tamara-heading-large;
  }

  &__description {
    @extend .tamara-title-small;

    margin-top: 12px;
    color: var(--tamara-color-functional-grey-dark-1);
  }

  &__input {
    --tamara-otp-color: var(--tamara-color-functional-grey-dark-2);

    display: flex;
    justify-content: flex-start;
    margin-top: 28px;

    &__pin-code {
      position: relative;
      display: inline-flex;
    }

    &.no-slash {
      &::v-deep {
        .vue-pincode-input-wrapper {
          .vue-pincode-input:nth-child(3) {
            margin-right: 0;
          }
        }
      }

      .tamara-login-input-otp__input__slash {
        display: none;
      }
    }

    &::v-deep .vue-pincode-input-wrapper {
      .vue-pincode-input {
        @extend .tamara-display-medium;

        width: 26px;
        max-width: 26px;
        padding: 0;
        margin: 0;
        font-family: 'Space Grotesk', sans-serif !important;
        color: var(--tamara-otp-color);
        background-color: transparent;
        border: none;
        box-shadow: none;

        &::placeholder {
          color: #949abf66;
        }

        &:nth-child(3) {
          margin-right: 34px;
        }

        &:disabled {
          color: var(--tamara-color-text-light-disabled);
        }
      }
    }

    &__slash {
      @extend .tamara-display-medium;

      position: absolute;
      top: 0;
      left: 87px;
      width: 16px;
      height: 100%;
      color: #949abf66;
    }

    &.filled-slash {
      .tamara-login-input-otp__input__slash {
        color: var(--tamara-color-functional-grey-dark-2);
      }

      &.is-disabled {
        --tamara-otp-color: var(--tamara-color-text-light-disabled);

        .tamara-login-input-otp__input__slash {
          color: var(--tamara-color-text-light-disabled);
        }
      }
    }
  }

  &__error {
    @extend .tamara-body-medium;

    display: flex;
    gap: 8px;
    align-items: center;
    margin-top: 28px;
    color: var(--tamara-color-error);
  }

  &__footer {
    margin-top: 28px;

    &__resend {
      @extend .tamara-label-small;

      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      align-items: center;
      justify-content: flex-start;
      color: var(--tamara-color-functional-grey-dark-1);

      &__link {
        color: var(--tamara-color-primary);
        cursor: pointer;

        &.is-disabled {
          color: var(--tamara-color-text-light-disabled);
          cursor: not-allowed;

          &:hover {
            text-decoration: none;
          }
        }

        &:hover {
          text-decoration: underline;
          text-decoration-thickness: 1px;
          text-decoration-color: var(--tamara-color-primary);
          text-underline-offset: 2px;
          opacity: 0.8;
        }
      }
    }
  }

  @include breakpoints(mobile) {
    padding: 40px;
    margin-bottom: var(--tamara-login-header-height);

    &__input {
      justify-content: center;
    }
  }
}
</style>
